import { useState } from 'react'
import { Star, ChevronDown, TrendingUp, TrendingDown } from 'lucide-react'
import type { RecentlyGradedItem } from '../../lib/api-types'

interface RecentlyGradedSectionProps {
  items: RecentlyGradedItem[]
}

function getGradeColor(percentage: number | null): string {
  if (percentage === null) return 'text-gray-400'
  if (percentage >= 90) return 'text-green-400'
  if (percentage >= 80) return 'text-blue-400'
  if (percentage >= 70) return 'text-yellow-400'
  if (percentage >= 60) return 'text-orange-400'
  return 'text-red-400'
}

function getGradeBg(percentage: number | null): string {
  if (percentage === null) return 'bg-surface-card/50'
  if (percentage >= 90) return 'bg-green-900/30'
  if (percentage >= 80) return 'bg-blue-900/30'
  if (percentage >= 70) return 'bg-yellow-900/20'
  return 'bg-red-900/20'
}

function formatTimeAgo(dateStr: string | null): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))

  if (diffHours < 1) return 'Just now'
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays === 1) return 'Yesterday'
  return `${diffDays}d ago`
}

export function RecentlyGradedSection({ items }: RecentlyGradedSectionProps) {
  const [expanded, setExpanded] = useState(false)

  if (!items || items.length === 0) {
    return null
  }

  // Separate celebration-worthy grades (90%+) from others
  const celebrations = items.filter(i => i.percentage !== null && i.percentage >= 90)
  const concerns = items.filter(i => i.percentage !== null && i.percentage < 70)
  const others = items.filter(i =>
    i.percentage !== null && i.percentage >= 70 && i.percentage < 90
  )

  // Show celebration banner if there are any 90%+ grades
  const hasCelebrations = celebrations.length > 0
  const hasConcerns = concerns.length > 0

  return (
    <div className="mb-4">
      {/* Celebration banner for excellent grades */}
      {hasCelebrations && (
        <div className="mb-3 p-3 rounded-lg bg-gradient-to-r from-green-900/40 to-emerald-900/30 border border-green-700/50">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
            <span className="text-sm font-medium text-green-300">
              {celebrations.length === 1 ? 'Great job!' : `${celebrations.length} excellent grades!`}
            </span>
          </div>
          <div className="space-y-1">
            {celebrations.slice(0, 3).map((item) => (
              <div key={item.id} className="flex items-center justify-between text-sm">
                <span className="text-white truncate flex-1 mr-2">{item.name}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-gray-400 text-xs">{item.course}</span>
                  <span className="font-bold text-green-400">
                    {item.percentage}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Concerns shown more subtly */}
      {hasConcerns && (
        <div className="mb-3 p-3 rounded-lg bg-red-900/20 border border-red-700/30">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-4 h-4 text-red-400" />
            <span className="text-sm font-medium text-red-300">Needs review</span>
          </div>
          <div className="space-y-1">
            {concerns.slice(0, 2).map((item) => (
              <div key={item.id} className="flex items-center justify-between text-sm">
                <span className="text-gray-300 truncate flex-1 mr-2">{item.name}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-gray-500 text-xs">{item.course}</span>
                  <span className="font-bold text-red-400">
                    {item.percentage}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Collapsible section for all other grades */}
      {others.length > 0 && (
        <div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center justify-between w-full text-left py-1"
          >
            <span className="text-sm text-gray-400 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              {others.length} other grade{others.length > 1 ? 's' : ''} posted
            </span>
            <ChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform ${
                expanded ? 'rotate-180' : ''
              }`}
            />
          </button>

          {expanded && (
            <div className="mt-2 space-y-1">
              {others.map((item) => (
                <div
                  key={item.id}
                  className={`flex items-center justify-between p-2 rounded-lg ${getGradeBg(item.percentage)}`}
                >
                  <div className="flex-1 min-w-0 mr-2">
                    <p className="text-sm font-medium text-white truncate">{item.name}</p>
                    <p className="text-xs text-gray-400">{item.course}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`font-bold ${getGradeColor(item.percentage)}`}>
                      {item.percentage}%
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.score}/{item.points_possible}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* If only celebrations/concerns shown, add a subtle timestamp */}
      {items.length > 0 && others.length === 0 && (
        <p className="text-xs text-gray-500 mt-1">
          {formatTimeAgo(items[0].graded_at)}
        </p>
      )}
    </div>
  )
}

export default RecentlyGradedSection
